import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Crown, Printer, CloudRain, Backpack, Sparkles, UtensilsCrossed, Car, CheckCircle2 } from "lucide-react";
import { http, eur } from "@/lib/api";
import { EscapadeHero, EscapadeOverview } from "@/components/EscapadeOverview";
import { Itinerary } from "@/components/Itinerary";

const Side = ({ icon: Icon, title, children, testid }) => (
  <div data-testid={testid} className="rounded-2xl bg-white border border-gray-200 p-6">
    <p className="flex items-center gap-2 font-semibold text-[#0B131E]"><Icon className="w-4 h-4 text-amber-600" />{title}</p>
    <div className="mt-3 text-sm text-gray-600 leading-relaxed space-y-2">{children}</div>
  </div>
);

export default function Premium() {
  const { token } = useParams();
  const [e, setE] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const { data } = await http.get(`/premium/${token}`);
        if (stop) return;
        if (data.status === "ready") return setE(data);
        setTimeout(load, 4000);
      } catch { if (!stop) setError(true); }
    };
    load();
    return () => { stop = true; };
  }, [token]);

  if (error) return <div data-testid="premium-error" className="py-24 text-center text-gray-600">Lien d'accès invalide ou expiré. <Link to="/acces" data-testid="premium-error-recover-link" className="text-amber-700 underline">Recevoir à nouveau mon lien</Link></div>;
  if (!e) return (
    <div data-testid="premium-loading" className="py-32 flex flex-col items-center text-center px-4">
      <Loader2 className="w-7 h-7 animate-spin text-amber-600" />
      <p className="mt-4 font-serif text-xl text-[#0B131E]">Préparation de votre itinéraire détaillé…</p>
      <p className="mt-1 text-sm text-gray-500">À la première ouverture, cela peut prendre jusqu'à une minute.</p>
    </div>
  );
  const p = e.premium || {};

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <span data-testid="premium-badge" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0B131E] text-amber-400 text-xs font-mono uppercase tracking-widest"><Crown className="w-3.5 h-3.5" />Accès Premium</span>
        <button onClick={() => window.print()} data-testid="premium-print-btn" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-amber-700 transition-colors"><Printer className="w-4 h-4" />Imprimer / PDF</button>
      </div>
      <EscapadeHero e={e} />
      <div className="mt-10 grid lg:grid-cols-[1.6fr_1fr] gap-10 items-start">
        <div className="space-y-12">
          <div>
            <h2 className="font-serif text-3xl font-bold text-[#0B131E] mb-8">Votre itinéraire détaillé</h2>
            <Itinerary days={p.itinerary} />
          </div>
          <EscapadeOverview e={e} detailedTransport />
        </div>
        <aside className="space-y-5 lg:sticky lg:top-24">
          {p.transport_details ? <Side icon={Car} title="Transport en détail" testid="premium-transport-details"><p>{p.transport_details}</p></Side> : null}
          {p.extra_activities?.length ? (
            <Side icon={Sparkles} title="Autres activités dans la zone" testid="premium-extra-activities">
              {p.extra_activities.map((a) => <p key={a.name}><span className="font-medium text-gray-900">{a.name}</span> · {eur(a.price)} — {a.description}</p>)}
            </Side>
          ) : null}
          {p.extra_restaurants?.length ? (
            <Side icon={UtensilsCrossed} title="Autres tables" testid="premium-extra-restaurants">
              {p.extra_restaurants.map((r) => <p key={r.name}><span className="font-medium text-gray-900">{r.name}</span> · {r.cuisine} · {r.price_range} <span className="text-gray-400">({r.meal})</span></p>)}
            </Side>
          ) : null}
          {p.rainy_day_plan ? <Side icon={CloudRain} title="Plan B si pluie" testid="premium-rainy-plan"><p>{p.rainy_day_plan}</p></Side> : null}
          {p.packing_list?.length ? (
            <Side icon={Backpack} title="À emporter" testid="premium-packing-list">
              {p.packing_list.map((x) => <p key={x} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />{x}</p>)}
            </Side>
          ) : null}
          {p.practical_tips?.length ? (
            <Side icon={Sparkles} title="Conseils pratiques" testid="premium-tips">
              {p.practical_tips.map((x) => <p key={x}>• {x}</p>)}
            </Side>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
